import React, { useState, useEffect, useRef } from 'react';
import {
    Grid, TextField, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, TablePagination, Checkbox,
    Dialog, DialogActions, DialogContent, DialogTitle, Autocomplete, IconButton,
    TableSortLabel, Box
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Science as ScienceIcon, 
         Group as GroupIcon, CreditCard as CreditCardIcon } from '@mui/icons-material';
import GestionPromocionesDAnalisis from './GestionPromocionesDAnalisis';
import GestionPromocionesDescuentoP from './GestionPromocionesDescuento';
import GestionPromocionesTarjeta from './GestionPromocionesTarjeta';
import { useUser } from '../contexts/UserContext';
import Swal from 'sweetalert2';
import { useAccesos } from '../contexts/AccesosContext';

function ListaPromociones() {
    const server = process.env.REACT_APP_API_ROUTE;
    const key = process.env.REACT_APP_API_KEY;
    const [promociones, setPromociones] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [selectedPromotion, setSelectedPromotion] = useState(null); // Solo una promoción seleccionada
    const [openModal, setOpenModal] = useState(false);
    const [sucursales, setSucursales] = useState([]);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
    const [tablaSucursales, setTablaSucursales] = useState([]);
    const [sucursalesEliminadas, setSucursalesEliminadas] = useState([]); // Arreglo de sucursales eliminadas
    const [openModifyModal, setOpenModifyModal] = useState(false);
    const [modifyComponent, setModifyComponent] = useState(null);
    // Estados para crear nuevas promociones
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [createComponent, setCreateComponent] = useState(null);
    const [modalMode, setModalMode] = useState('edit'); // 'edit' o 'create'
    // Estados para ordenamiento
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    const { user } = useUser();
    const { tienePermiso } = useAccesos();
    const firstRender = useRef(true);

    useEffect(() => {
        // Establecer fechas iniciales al cargar el componente
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        // Format dates as YYYY-MM-DD
        const desde = firstDayOfMonth.toISOString().split('T')[0];
        const hasta = lastDayOfMonth.toISOString().split('T')[0];
        
        setFechaDesde(desde);
        setFechaHasta(hasta);
        setSearchQuery(''); // Inicializar búsqueda vacía

        // Cargar promociones con las fechas iniciales y búsqueda vacía
        fetchPromociones('', desde, hasta);
    }, []);

    useEffect(() => {
        fetchSucursales();
    }, []);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (!openModifyModal) {
            fetchPromociones(searchQuery, fechaDesde, fechaHasta);
        }
    }, [openModifyModal]);

    // Función unificada para cerrar modales y manejar guardado
    const handleCloseModifyModal = (shouldRefresh = true) => {
        setOpenModifyModal(false);
        setOpenCreateModal(false);
        setModifyComponent(null);
        setCreateComponent(null);
        setModalMode(null);
        // Limpiar selección solo si estamos en modo crear
        if (modalMode === 'create') {
            setSelectedPromotion(null);
        }
        // Recargar promociones después de cerrar el modal si es necesario
        if (shouldRefresh) {
            fetchPromociones(searchQuery, fechaDesde, fechaHasta);
        }
    };

    // Función específica para manejar guardado exitoso
    const handleSaveSuccess = () => {
        // Mostrar mensaje de éxito
        Swal.fire({
            title: '¡Éxito!',
            text: modalMode === 'create' ? 'Promoción creada correctamente' : 'Promoción actualizada correctamente',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
        });
        
        // Cerrar modal y refrescar datos
        handleCloseModifyModal(true);
    };

    // Función para manejar cancelación
    const handleCancel = () => {
        // Cerrar modal sin refrescar datos
        handleCloseModifyModal(false);
    };

    // Funciones para crear nuevas promociones
    const handleCrearPromocion = (tipo) => {
        // Mapeo directo de tipo string a component type
        const tipoComponentMap = {
            'tipo1': 'tipo1', // Promoción por Análisis
            'tipo2': 'tipo2', // Promoción por Perfil  
            'tipo3': 'tipo3'  // Promoción por Tarjeta Bancaria
        };

        const componentType = tipoComponentMap[tipo];
        
        if (componentType) {
            setCreateComponent(componentType);
            setModalMode('create');
            setOpenCreateModal(true);
        } else {
            Swal.fire('Error', `Tipo de promoción no reconocido: ${tipo}`, 'error');
        }
    };


    const fetchPromociones = async (busqueda = '', desde = fechaDesde, hasta = fechaHasta) => {
        try {
            const response = await fetch(`${server}/PrPromociones/promociones`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    search: busqueda,
                    desde,
                    hasta
                })
            });
            const data = await response.json();
            console.log(data);
            const nuevas = data.$values || [];
            setPromociones(data.$values || []);


            if (selectedPromotion) {
                const reSel = nuevas.find(p => p.id === selectedPromotion.id);
                setSelectedPromotion(reSel || null);
            }

        } catch (error) {
            console.error('Error al cargar las promociones:', error);
        }
    };

    const fetchSucursales = async () => {
        try {
            const response = await fetch(`${server}/MgSucursales`);
            const data = await response.json();
            setSucursales(data.$values || []);
        } catch (error) {
            console.error('Error al cargar las sucursales:', error);
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Función para manejar el ordenamiento de las columnas
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Función para comparar valores para el ordenamiento
    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const descendingComparator = (a, b, orderBy) => {
        // Manejar valores especiales para fechas
        if (orderBy === 'fechaDesde' || orderBy === 'fechaHasta') {
            const dateA = new Date(a[orderBy]);
            const dateB = new Date(b[orderBy]);
            if (dateB < dateA) return -1;
            if (dateB > dateA) return 1;
            return 0;
        }
        
        // Manejar códigos numéricos
        if (orderBy === 'codigo') {
            const numA = parseInt(a[orderBy]) || 0;
            const numB = parseInt(b[orderBy]) || 0;
            if (numB < numA) return -1;
            if (numB > numA) return 1;
            return 0;
        }
        
        // Manejar sucursales (array)
        if (orderBy === 'sucursales') {
            const sucursalesA = a.sucursales?.$values?.join(', ') || '';
            const sucursalesB = b.sucursales?.$values?.join(', ') || '';
            if (sucursalesB < sucursalesA) return -1;
            if (sucursalesB > sucursalesA) return 1;
            return 0;
        }
        
        // Ordenamiento de texto normal
        const valueA = (a[orderBy] || '').toString().toLowerCase();
        const valueB = (b[orderBy] || '').toString().toLowerCase();
        if (valueB < valueA) return -1;
        if (valueB > valueA) return 1;
        return 0;
    };

    // Función para aplicar ordenamiento estable
    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);  // Reiniciar la paginación
    };

    // Manejar selección única de promoción
    const handleSelectPromotion = (promo) => {
        console.log('Promoción seleccionada:', promo);
        setSelectedPromotion((prevSelectedPromotion) => {
            if (prevSelectedPromotion && prevSelectedPromotion.id === promo.id) {
                // Deseleccionar si se hace clic en la misma promoción
                return null;
            } else {
                // Seleccionar la nueva promoción
                return promo;
            }
        });
    };

    // Abrir modal para sucursales y cargar sucursales de la promoción seleccionada
    const handleOpenSucursalesModal = async () => {
        if (!selectedPromotion) {
            Swal.fire('Atención', 'Por favor, selecciona una promoción.', 'warning');
            return;
        }
        
        try {
            const response = await fetch(`${server}/PrPromocionesXSucursales/byidpromocion/${selectedPromotion.id}`);
            const data = await response.json();
            
            console.log('Respuesta API sucursales:', data);
            const sucursalesFromAPI = data.$values || [];
            
            // Mapear correctamente las sucursales con toda la información necesaria
            const sucursalesConMarca = sucursalesFromAPI.map(item => ({
                id: item.id, // ID de la relación PrPromocionesXSucursales
                idSucursal: item.idSucursal,
                idPromocion: item.idPromocion,
                nombreSucursal: item.nombreSucursal || `Sucursal ${item.idSucursal}`,
                fromAPI: true
            }));
            
            console.log('Sucursales procesadas:', sucursalesConMarca);
            setTablaSucursales(sucursalesConMarca);
        } catch (error) {
            console.error('Error al cargar las sucursales de la promoción:', error);
            Swal.fire('Error', 'No se pudieron cargar las sucursales de la promoción.', 'error');
        }
        
        // Reiniciar el arreglo de sucursales eliminadas
        setSucursalesEliminadas([]);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSucursalSeleccionada(null);
        setTablaSucursales([]);
        setSucursalesEliminadas([]); // Limpiar sucursales eliminadas al cerrar el modal
    };

    const handleAgregarSucursal = () => {
        if (!sucursalSeleccionada) {
            Swal.fire('Atención', 'Por favor, selecciona una sucursal.', 'warning');
            return;
        }

        // Verificar si la sucursal ya está en la tabla
        const exists = tablaSucursales.some(item => item.idSucursal === sucursalSeleccionada.idSucursal);
        if (exists) {
            Swal.fire('Información', 'La sucursal ya está agregada.', 'info');
            setSucursalSeleccionada(null);
            return;
        }

        // Agregar la sucursal con fromAPI: false (nueva)
        const nuevaSucursal = {
            id: null, // No tiene ID de relación porque es nueva
            idSucursal: sucursalSeleccionada.idSucursal,
            idPromocion: selectedPromotion.id,
            nombreSucursal: sucursalSeleccionada.nombreSucursal,
            fromAPI: false
        };

        setTablaSucursales(prevState => [...prevState, nuevaSucursal]);
        setSucursalSeleccionada(null);
        
        console.log('Sucursal agregada:', nuevaSucursal);
    };

    const handleEliminarSucursal = (idSucursal) => {
        console.log('Eliminando sucursal con idSucursal:', idSucursal);
        
        setTablaSucursales((prevState) => {
            const sucursalEliminada = prevState.find(item => item.idSucursal === idSucursal);
            console.log('Sucursal encontrada para eliminar:', sucursalEliminada);
            
            if (!sucursalEliminada) {
                console.error('No se encontró la sucursal a eliminar');
                return prevState;
            }

            // Si la sucursal proviene de la API (existe en BD), la marcamos para eliminación
            if (sucursalEliminada.fromAPI && sucursalEliminada.id) {
                setSucursalesEliminadas((prevEliminadas) => {
                    const nuevasEliminadas = [...prevEliminadas, sucursalEliminada];
                    console.log('Sucursales marcadas para eliminar:', nuevasEliminadas);
                    return nuevasEliminadas;
                });
            }

            // Remover la sucursal del arreglo tablaSucursales
            const nuevaTabla = prevState.filter(item => item.idSucursal !== idSucursal);
            console.log('Nueva tabla de sucursales:', nuevaTabla);
            return nuevaTabla;
        });
    };

    const handleGuardarSucursales = async () => {
        try {
            let operacionesExitosas = 0;
            let operacionesTotales = 0;

            // Procesar sucursales nuevas (agregar)
            const sucursalesAgregadas = tablaSucursales.filter(item => !item.fromAPI);
            operacionesTotales += sucursalesAgregadas.length;

            if (sucursalesAgregadas.length > 0) {
                console.log('Agregando sucursales:', sucursalesAgregadas);
                
                for (const sucursal of sucursalesAgregadas) {
                    try {
                        const dataSucursal = {
                            idPromocion: selectedPromotion.id,
                            idSucursal: sucursal.idSucursal,
                            ingresadoPor: user.codigoUsuario
                        };
                        
                        console.log('Enviando datos para agregar:', dataSucursal);
                        
                        const response = await fetch(`${server}/PrPromocionesXSucursales`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(dataSucursal),
                        });
                        
                        if (response.ok) {
                            operacionesExitosas++;
                            console.log(`Sucursal ${sucursal.idSucursal} agregada exitosamente`);
                        } else {
                            const errorText = await response.text();
                            console.error(`Error al agregar sucursal ${sucursal.idSucursal}:`, errorText);
                        }
                    } catch (error) {
                        console.error(`Error al agregar sucursal ${sucursal.idSucursal}:`, error);
                    }
                }
            }

            // Procesar sucursales eliminadas
            operacionesTotales += sucursalesEliminadas.length;

            if (sucursalesEliminadas.length > 0) {
                console.log('Eliminando sucursales:', sucursalesEliminadas);
                
                for (const sucursal of sucursalesEliminadas) {
                    try {
                        if (!sucursal.id) {
                            console.warn('Sucursal sin ID de relación, saltando eliminación:', sucursal);
                            continue;
                        }
                        
                        console.log(`Eliminando relación con ID: ${sucursal.id}`);
                        const response = await fetch(`${server}/PrPromocionesXSucursales/${sucursal.id}/${user.codigoUsuario}`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                        });
                        
                        if (response.ok) {
                            operacionesExitosas++;
                            console.log(`Relación ${sucursal.id} eliminada exitosamente`);
                        } else {
                            const errorText = await response.text();
                            console.error(`Error al eliminar relación ${sucursal.id}:`, errorText);
                        }
                    } catch (error) {
                        console.error(`Error al eliminar relación ${sucursal.id}:`, error);
                    }
                }
            }

            // Mostrar resultado
            if (operacionesExitosas === operacionesTotales) {
                await Swal.fire('¡Éxito!', 'Sucursales actualizadas correctamente.', 'success');
            } else if (operacionesExitosas > 0) {
                await Swal.fire('Parcialmente completado', 
                    `Se completaron ${operacionesExitosas} de ${operacionesTotales} operaciones.`, 'warning');
            } else {
                await Swal.fire('Error', 'No se pudieron realizar las operaciones.', 'error');
            }

            // Recargar datos y cerrar modal
            fetchPromociones(searchQuery, fechaDesde, fechaHasta);
            handleCloseModal();

        } catch (error) {
            console.error('Error general al guardar sucursales:', error);
            Swal.fire('Error', 'Ocurrió un problema al actualizar las sucursales.', 'error');
        }
    };


    // Función unificada para manejar la modificación de promociones
    const handleModificarPromociones = async () => {
        if (!selectedPromotion) {
            alert('Por favor, selecciona una promoción.');
            return;
        }

        // Mapeo unificado de tipos de promoción
        const tipoComponentMap = {
            1: 'tipo1', // Promoción por Análisis (GestionPromocionesDescuentoA)
            2: 'tipo2', // Promoción por Perfil (GestionPromocionesDescuento)
            3: 'tipo3'  // Promoción por Tarjeta Bancaria (GestionPromocionesTarjeta)
        };

        const componentType = tipoComponentMap[selectedPromotion.idTipo];

        if (!componentType) {
            Swal.fire('Error', `Tipo de promoción no reconocido: ${selectedPromotion.idTipo}`, 'error');
            return;
        }

        try {
            // Obtener el detalle completo de la promoción para tener todos los campos
            const response = await fetch(`${server}/PrPromociones/${selectedPromotion.id}`);
            if (response.ok) {
                const detalle = await response.json();
                setSelectedPromotion(detalle);
            }
        } catch (error) {
            console.error('Error al cargar detalle de la promoción:', error);
        }

        setModifyComponent(componentType);
        setModalMode('edit');
        setOpenModifyModal(true);
    };

    // Función para obtener el nombre del tipo de promoción
    const getTipoPromocionName = (idTipo) => {
        const tipoNames = {
            1: 'Descuento por Análisis',
            2: 'Descuento por Perfil', 
            3: 'Descuento por Tarjeta Bancaria'
        };
        return tipoNames[idTipo] || 'Tipo Desconocido';
    };

    // Función para obtener el icono del tipo de promoción
    const getTipoPromocionIcon = (idTipo) => {
        const iconMap = {
            1: <ScienceIcon fontSize="small" />,
            2: <GroupIcon fontSize="small" />,
            3: <CreditCardIcon fontSize="small" />
        };
        return iconMap[idTipo] || null;
    };

    // Función unificada para renderizar componentes de promoción (crear/editar)
    const renderPromocionComponent = () => {
        const isEditMode = modalMode === 'edit';
        const componentType = isEditMode ? modifyComponent : createComponent;
        const promotion = isEditMode ? selectedPromotion : null;

        const componentProps = {
            promotion: promotion,
            onCancel: handleCancel,
            onSave: handleSaveSuccess,
            isEditMode: isEditMode
        };

        switch (componentType) {
            case 'tipo1':
                return <GestionPromocionesDAnalisis {...componentProps} />;
            case 'tipo2':
                return <GestionPromocionesDescuentoP {...componentProps} />;
            case 'tipo3':
                return <GestionPromocionesTarjeta {...componentProps} />;
            default:
                return (
                    <Typography variant="body1" color="error">
                        Componente no encontrado para el tipo: {componentType}
                    </Typography>
                );
        }
    };

    // Función para obtener el título del modal
    const getModalTitle = () => {
        if (modalMode === 'create') {
            const tipoInfo = {
                'tipo1': { name: 'Descuento por Análisis', icon: <ScienceIcon /> },
                'tipo2': { name: 'Descuento por Perfil', icon: <GroupIcon /> },
                'tipo3': { name: 'Descuento por Tarjeta Bancaria', icon: <CreditCardIcon /> }
            };
            const info = tipoInfo[createComponent];
            return (
                <>
                    {info?.icon}
                    <Typography variant="h6" component="span" sx={{ ml: 1 }}>
                        {`Crear Nueva Promoción - ${info?.name || ''}`}
                    </Typography>
                </>
            );
        } else {
            const icon = getTipoPromocionIcon(selectedPromotion?.idTipo);
            return (
                <>
                    {icon}
                    <Typography variant="h6" component="span" sx={{ ml: 1 }}>
                        {`Modificar Promoción - ${selectedPromotion ? getTipoPromocionName(selectedPromotion.idTipo) : ''}`}
                    </Typography>
                </>
            );
        }
    };

    // Función para manejar la acción de anular promociones
    const handleAnularPromociones = async () => {
        if (!selectedPromotion) {
            Swal.fire('¡Espera!', 'Por favor, selecciona una promoción.', 'info');
            return;
        }

        const { value: confirmar } = await Swal.fire({
            title: `¿Anular "${selectedPromotion.descripcion}"?`,
            text: '¡No podrás deshacer esto!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, anular',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        });

        if (!confirmar) return;

        try {
            const username = '' + user.codigoUsuario + '/';
            const url = `${server}/PrPromociones/${selectedPromotion.id}/${username}`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': key
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Status ${response.status}: ${errorText}`);
            }

            await Swal.fire('¡Listo!', 'La promoción ha sido anulada.', 'success');
            fetchPromociones(searchQuery, fechaDesde, fechaHasta);
            setSelectedPromotion(null);

        } catch (error) {
            console.error('Error al anular la promoción:', error);
            Swal.fire('Error', 'Ocurrió un problema al anular la promoción. Revisa la consola.', 'error');
        }
    };

    const handleBuscarPromociones = () => {
        // Validar que las fechas estén seleccionadas
        if (!fechaDesde || !fechaHasta) {
            alert('Por favor, selecciona las fechas de inicio y fin.');
            return;
        }
        fetchPromociones(searchQuery);
        setSearchQuery(''); // Limpiar búsqueda al buscar promociones
    };

    // Filtrar y ordenar promociones
    const filteredAndSortedPromociones = React.useMemo(() => {
        // Primero filtrar
        const filtered = promociones.filter((promo) => {
            const searchLower = searchQuery.toLowerCase();
            return (
                promo.codigo?.toString().toLowerCase().includes(searchLower) ||
                promo.descripcion?.toLowerCase().includes(searchLower) ||
                promo.tipoPromocion?.toLowerCase().includes(searchLower) ||
                getTipoPromocionName(promo.idTipo)?.toLowerCase().includes(searchLower) ||
                promo.sucursales?.$values?.some(sucursal => sucursal.toLowerCase().includes(searchLower))
            );
        });

        // Luego ordenar si hay un criterio de ordenamiento activo
        if (orderBy) {
            return stableSort(filtered, getComparator(order, orderBy));
        }
        
        return filtered;
    }, [promociones, searchQuery, order, orderBy]);

    return (
        <>
            <div className="right_col" role="main">
                <div className="x_title"></div>
                <div className="x_content">
                    <br />
                    {/* <div className="row"> */}
                    {/* <div className="col-md-9 col-sm-12 offset-md-2"> */}
                    <Typography variant="h6" gutterBottom>
                        Promociones para Análisis Clínicos
                    </Typography>

                    {/* Botones para crear nuevas promociones */}
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            Crear Nueva Promoción
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleCrearPromocion('tipo1')}
                                    startIcon={<ScienceIcon />}
                                    disabled={!tienePermiso('Crear Promoción Análisis')}
                                    sx={{
                                        backgroundColor: '#1976d2',
                                        '&:hover': { backgroundColor: '#1565c0' }
                                    }}
                                >
                                    Descuento por Análisis
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => handleCrearPromocion('tipo2')}
                                    startIcon={<GroupIcon />}
                                    disabled={!tienePermiso('Crear Promoción Perfil')}
                                    sx={{
                                        backgroundColor: '#7b1fa2',
                                        color: 'white',
                                        '&:hover': { backgroundColor: '#6a1b9a' }
                                    }}
                                >
                                    Descuento por Perfil
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={() => handleCrearPromocion('tipo3')}
                                    startIcon={<CreditCardIcon />}
                                    disabled={!tienePermiso('Crear Promoción Tarjeta')}
                                    sx={{
                                        backgroundColor: '#388e3c',
                                        color: 'white',
                                        '&:hover': { backgroundColor: '#2e7d32' }
                                    }}
                                >
                                    Descuento por Tarjeta Bancaria
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Búsqueda Rápida */}
                    <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
                        <Grid item xs={6}>
                            <TextField
                                label="Búsqueda Rápida"
                                fullWidth
                                variant="outlined"
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField
                                label="Desde"
                                type="date"
                                fullWidth
                                variant="outlined"
                                value={fechaDesde}
                                onChange={(e) => setFechaDesde(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField
                                label="Hasta"
                                type="date"
                                fullWidth
                                variant="outlined"
                                value={fechaHasta}
                                onChange={(e) => setFechaHasta(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button variant="contained" color="primary" onClick={handleBuscarPromociones}>
                                Buscar
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Tabla de Promociones */}
                    <TableContainer component={Paper} style={{ overflowX: 'auto' }}>
                        <Table style={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Seleccionar</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'codigo'}
                                            direction={orderBy === 'codigo' ? order : 'asc'}
                                            onClick={() => handleRequestSort('codigo')}
                                        >
                                            Código Promoción
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'descripcion'}
                                            direction={orderBy === 'descripcion' ? order : 'asc'}
                                            onClick={() => handleRequestSort('descripcion')}
                                        >
                                            Descripción
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'fechaDesde'}
                                            direction={orderBy === 'fechaDesde' ? order : 'asc'}
                                            onClick={() => handleRequestSort('fechaDesde')}
                                        >
                                            Fecha Inicio
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'fechaHasta'}
                                            direction={orderBy === 'fechaHasta' ? order : 'asc'}
                                            onClick={() => handleRequestSort('fechaHasta')}
                                        >
                                            Fecha Fin
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'tipoPromocion'}
                                            direction={orderBy === 'tipoPromocion' ? order : 'asc'}
                                            onClick={() => handleRequestSort('tipoPromocion')}
                                        >
                                            Tipo de Promoción
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'sucursales'}
                                            direction={orderBy === 'sucursales' ? order : 'asc'}
                                            onClick={() => handleRequestSort('sucursales')}
                                        >
                                            Sucursales donde aplica la promoción
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAndSortedPromociones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((promo) => (
                                    <TableRow 
                                        key={promo.id}
                                        hover
                                        selected={selectedPromotion && selectedPromotion.id === promo.id}
                                        style={{
                                            backgroundColor: selectedPromotion && selectedPromotion.id === promo.id ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                                        }}
                                    >
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedPromotion && selectedPromotion.id === promo.id}
                                                onChange={() => handleSelectPromotion(promo)}
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {promo.codigo}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {promo.descripcion}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {promo.fechaDesde ? (() => {
                                                    const fecha = promo.fechaDesde.split('T')[0];
                                                    const [year, month, day] = fecha.split('-');
                                                    return `${day}/${month}/${year}`;
                                                })() : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {promo.fechaHasta ? (() => {
                                                    const fecha = promo.fechaHasta.split('T')[0];
                                                    const [year, month, day] = fecha.split('-');
                                                    return `${day}/${month}/${year}`;
                                                })() : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box 
                                                display="flex" 
                                                alignItems="center" 
                                                gap={1}
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    backgroundColor: promo.idTipo === 1 ? '#e3f2fd' : 
                                                                   promo.idTipo === 2 ? '#f3e5f5' : '#e8f5e8',
                                                    color: promo.idTipo === 1 ? '#1976d2' : 
                                                           promo.idTipo === 2 ? '#7b1fa2' : '#388e3c',
                                                    display: 'inline-flex',
                                                    maxWidth: 'fit-content'
                                                }}
                                            >
                                                {getTipoPromocionIcon(promo.idTipo)}
                                                <Typography variant="body2" component="span">
                                                    {getTipoPromocionName(promo.idTipo)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {promo.sucursales?.$values?.join(', ') || 'Sin sucursales asignadas'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredAndSortedPromociones.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="textSecondary">
                                                No se encontraron promociones con los criterios especificados
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredAndSortedPromociones.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                        />
                    </TableContainer>

                    {/* Botones de acción */}
                    <Grid container spacing={2} justifyContent="flex-end" style={{ marginTop: 20 }}>
                        <Grid item>
                            <Button variant="contained" color="primary" onClick={handleOpenSucursalesModal}>
                                Sucursales
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="secondary" onClick={handleModificarPromociones} disabled={!tienePermiso('Editar Promoción')}>
                                Modificar
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="error" onClick={handleAnularPromociones} disabled={!tienePermiso('Eliminar Promoción')}>
                                Anular
                            </Button>
                        </Grid>
                    </Grid>
                    {/* </div> */}
                    {/* </div> */}

                    {/* Modal de Sucursales */}
                    <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                        <DialogTitle>Sucursales de la Promoción</DialogTitle>
                        <DialogContent>
                            <Autocomplete
                                options={sucursales}
                                getOptionLabel={(option) => option.nombreSucursal || ''}
                                value={sucursalSeleccionada}
                                onChange={(event, newValue) => setSucursalSeleccionada(newValue)}
                                renderInput={(params) => <TextField {...params} label="Agregar Sucursal" variant="outlined" fullWidth />}
                            />
                            <Button variant="contained" color="primary" onClick={handleAgregarSucursal} style={{ marginTop: 10 }}>
                                Agregar Sucursal
                            </Button>
                            <TableContainer component={Paper} style={{ marginTop: 20 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sucursal</TableCell>
                                            <TableCell align="right">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tablaSucursales.map((item) => (
                                            <TableRow key={item.idSucursal}>
                                                <TableCell>{item.nombreSucursal}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton onClick={() => handleEliminarSucursal(item.idSucursal)}>
                                                        <DeleteIcon color="error" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseModal} color="secondary">
                                Cerrar
                            </Button>
                            <Button onClick={handleGuardarSucursales} color="primary">
                                Guardar
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {/* Modal unificado para crear/modificar promociones */}
                    <Dialog open={openModifyModal || openCreateModal} onClose={handleCloseModifyModal} maxWidth="md" fullWidth>
                        <DialogTitle>
                            <Box display="flex" alignItems="center" gap={1}>
                                {getModalTitle()}
                            </Box>
                            {modalMode === 'edit' && selectedPromotion && (
                                <Typography variant="body2" color="textSecondary">
                                    {selectedPromotion.descripcion}
                                </Typography>
                            )}
                        </DialogTitle>
                        <DialogContent>
                            {(modalMode === 'edit' && selectedPromotion) || (modalMode === 'create' && createComponent) ? 
                                renderPromocionComponent() : (
                                <Typography variant="body1" color="error">
                                    {modalMode === 'edit' ? 'No hay promoción seleccionada' : 'No se pudo determinar el tipo de promoción'}
                                </Typography>
                            )}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </>
    );
}

export default ListaPromociones;
